import * as React from "react";
import { Search, X, Loader2 } from "lucide-react";
import { cn } from "lib/utils";

export interface SearchInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "size"> {
    value: string;
    /** Fires immediately on every keystroke */
    onChange: (value: string) => void;
    /**
     * Fires only after `debounceMs` of quiet. Use this for the query that hits
     * the API; use `onChange` for the controlled input value.
     */
    onDebouncedChange?: (value: string) => void;
    debounceMs?: number;
    /** Shows a spinner while a request is in flight */
    isSearching?: boolean;
    size?: "sm" | "md";
    /** Renders the ⌘K hint on the right when the field is empty and unfocused */
    shortcutHint?: string;
    wrapperClassName?: string;
}

/**
 * The one search field. Campaigns, Leads, Audiences, Templates, History and
 * Support each shipped their own copy of this markup with its own debounce
 * logic; this consolidates the behaviour too — clear button, Escape to clear,
 * debounce, in-flight indicator and correct `type="search"` semantics.
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
    (
        {
            value,
            onChange,
            onDebouncedChange,
            debounceMs = 300,
            isSearching = false,
            size = "md",
            shortcutHint,
            placeholder = "Search…",
            className,
            wrapperClassName,
            disabled,
            ...props
        },
        forwardedRef
    ) => {
        const innerRef = React.useRef<HTMLInputElement>(null);
        React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLInputElement);

        const [focused, setFocused] = React.useState(false);

        // Debounced mirror of `value`. Kept in a ref so changing the callback
        // identity between renders never restarts the timer.
        const callbackRef = React.useRef(onDebouncedChange);
        callbackRef.current = onDebouncedChange;
        const firstRun = React.useRef(true);

        React.useEffect(() => {
            if (!callbackRef.current) return;
            if (firstRun.current) {
                firstRun.current = false;
                return;
            }
            const id = setTimeout(() => callbackRef.current?.(value), debounceMs);
            return () => clearTimeout(id);
        }, [value, debounceMs]);

        const clear = () => {
            onChange("");
            callbackRef.current?.("");
            innerRef.current?.focus();
        };

        const height = size === "sm" ? "h-9 text-meta" : "h-10 text-ui";
        const showHint = shortcutHint && !value && !focused;

        return (
            <div className={cn("relative flex-1 min-w-0", wrapperClassName)}>
                <Search
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none"
                    aria-hidden="true"
                />
                <input
                    ref={innerRef}
                    type="search"
                    role="searchbox"
                    value={value}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape" && value) {
                            e.preventDefault();
                            e.stopPropagation();
                            clear();
                        }
                        props.onKeyDown?.(e);
                    }}
                    className={cn(
                        "w-full pl-9 pr-9 rounded-xl font-medium",
                        "bg-surface-sunken border border-line text-content placeholder:text-content-subtle",
                        "transition-[border-color,box-shadow,background-color] duration-150 ease-ds",
                        "hover:border-line-strong",
                        "focus:outline-none focus:bg-surface focus:border-brand focus:shadow-focus",
                        "disabled:opacity-60 disabled:cursor-not-allowed",
                        // Kill the native WebKit search decorations — we draw our own
                        "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none",
                        height,
                        className
                    )}
                    {...props}
                />

                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                    {isSearching && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-brand" aria-hidden="true" />
                    )}
                    {showHint && (
                        <kbd
                            className="hidden sm:inline-flex items-center h-5 px-1.5 rounded border border-line bg-surface text-2xs font-sans font-semibold text-content-subtle pointer-events-none"
                            aria-hidden="true"
                        >
                            {shortcutHint}
                        </kbd>
                    )}
                    {value && !isSearching && (
                        <button
                            type="button"
                            onClick={clear}
                            className="p-0.5 rounded-md text-content-subtle hover:text-content hover:bg-surface-hover transition-colors focus-ring"
                            aria-label="Clear search"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        );
    }
);
SearchInput.displayName = "SearchInput";

export default SearchInput;
